// Real PDF files for the History Graph & Map exports: the export HTML is rendered
// in a hidden iframe, the imagery is captured (chart canvas directly, map via
// html2canvas with CORS tiles) and composed into a jsPDF document.
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { loadLogoPng } from './tripExport';

const SLATE_900 = [15, 23, 42];
const SLATE_600 = [71, 85, 105];
const SLATE_500 = [100, 116, 139];

const hex2rgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return [71, 85, 105];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const renderInIframe = (html, { width, height, timeout = 15000 }) => new Promise((resolve) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = `position:fixed;left:-20000px;top:0;width:${width}px;height:${height}px;border:0;visibility:visible;`;
  document.body.appendChild(iframe);
  iframe.srcdoc = html;
  const started = Date.now();
  const poll = () => {
    try {
      if (iframe.contentWindow?.__exportReady) return resolve(iframe);
    } catch { /* cross-frame access hiccup — keep polling */ }
    if (Date.now() - started > timeout) return resolve(iframe); // best effort
    setTimeout(poll, 250);
  };
  setTimeout(poll, 400);
});

/**
 * Renders `html` off-screen, captures the imagery and saves a real PDF file.
 * captureCanvas: CSS selector of a <canvas> to grab directly (crisp charts).
 * captureElement: CSS selector of an element to rasterise via html2canvas (maps).
 */
export const exportImagePdf = async ({
  html, width = 1400, height = 860,
  captureCanvas, captureElement,
  title, subtitle, metaLines = [], legendItems = [],
  filename, generatedAt, extraWaitMs = 800,
}) => {
  toast.info('Generating PDF…');
  let iframe;
  try {
    iframe = await renderInIframe(html, { width, height });
    if (extraWaitMs) await new Promise((r) => setTimeout(r, extraWaitMs));
    let imgData, imgW, imgH, imgType;
    if (captureCanvas) {
      const c = iframe.contentDocument.querySelector(captureCanvas);
      if (!c) throw new Error('chart canvas not found');
      // Composite onto white (chart canvas is transparent) and use JPEG to keep the file small.
      const flat = document.createElement('canvas');
      flat.width = c.width; flat.height = c.height;
      const ctx = flat.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, flat.width, flat.height);
      ctx.drawImage(c, 0, 0);
      imgData = flat.toDataURL('image/jpeg', 0.92);
      imgW = c.width; imgH = c.height; imgType = 'JPEG';
    } else {
      const el = iframe.contentDocument.querySelector(captureElement) || iframe.contentDocument.body;
      const canvas = await html2canvas(el, {
        useCORS: true, backgroundColor: '#ffffff', scale: 1.5, logging: false,
        windowWidth: width, windowHeight: height,
      });
      imgData = canvas.toDataURL('image/jpeg', 0.92);
      imgW = canvas.width; imgH = canvas.height; imgType = 'JPEG';
    }

    const logo = await loadLogoPng();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const M = 32;
    let y = M;
    if (logo) {
      const h = 26, w = logo.w * (h / logo.h);
      try { doc.addImage(logo.dataUrl, 'PNG', M, y, w, h); } catch { /* skip logo */ }
    }
    doc.setFontSize(14); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
    doc.text(title, PAGE_W - M, y + 12, { align: 'right' });
    if (subtitle) {
      doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
      doc.text(subtitle, PAGE_W - M, y + 26, { align: 'right' });
    }
    y += 34;
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(1); doc.line(M, y, PAGE_W - M, y);
    y += 6;
    doc.setFontSize(8.5); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
    metaLines.forEach((l) => { doc.text(l, M, y + 8); y += 12; });
    if (legendItems.length) {
      let x = M;
      legendItems.forEach((it) => {
        const wLabel = doc.getTextWidth(it.label);
        if (x + 10 + wLabel > PAGE_W - M) { x = M; y += 12; }
        doc.setFillColor(...hex2rgb(it.color));
        doc.rect(x, y + 1.5, 7, 7, 'F');
        doc.setTextColor(...SLATE_600);
        doc.text(it.label, x + 10, y + 8);
        x += 10 + wLabel + 16;
      });
      y += 14;
    }
    y += 4;
    const availW = PAGE_W - 2 * M;
    const availH = PAGE_H - y - 30;
    const sc = Math.min(availW / imgW, availH / imgH);
    const dw = imgW * sc, dh = imgH * sc;
    doc.addImage(imgData, imgType, M + (availW - dw) / 2, y, dw, dh);
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_500);
    doc.text(`Generated by EnsueLIVETRAK - ${generatedAt}`, PAGE_W / 2, PAGE_H - 14, { align: 'center' });
    doc.save(filename);
    toast.success('PDF downloaded');
  } catch (e) {
    console.error('Image PDF export failed:', e);
    toast.error('PDF export failed');
  } finally {
    iframe?.remove();
  }
};
